import type React from 'react';

import { Redirect } from '@change-corgi/core-react/router';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import { useIsCommentAfterVideoArm, useVideoFirstFlow } from '#src/app/pages/actions/shared/hooks';
import type { VideoConfirmationNextStep } from '#src/app/shared/utils/supporterVoicesFlow';
import { buildVideoConfirmationUrl } from '#src/app/shared/utils/supporterVoicesFlow';

import { useVideosComponentContext } from '../../context';
import { VideoRecorder } from '../VideoRecorder';
import { VideoUpload } from '../VideoUpload';
import { Welcome } from '../Welcome';

import { useVideoSteps } from './hooks/useVideoSteps';

export const VideoSteps: React.FC = () => {
	const { uploadStep } = useVideoSteps();
	const {
		data: {
			petition: { slug },
		},
	} = usePetitionActionsPageData();
	const {
		data: { uploadedVideoId },
	} = useVideosComponentContext();
	const {
		data: { hasCommented },
	} = useVideoFirstFlow();
	const isCommentAfterVideoArm = useIsCommentAfterVideoArm();

	/*
	 * Control gets no onward step at all - its flow ends at the confirmation, unchanged.
	 *
	 * On the arms that put the comment after the video, a supporter who has not commented is sent to
	 * the comment step. One who already has - they wrote a comment first and then took the record CTA
	 * from the post-comment screen - is sent onward instead, because asking again would drop them back
	 * on an empty comment form for a comment they already wrote.
	 */
	const nextStep: VideoConfirmationNextStep | undefined = (() => {
		if (!isCommentAfterVideoArm) return undefined;
		return hasCommented ? 'done' : 'comment';
	})();

	return (
		<>
			{uploadStep === 'ready' && <Welcome />}
			{uploadStep === 'recorder' && <VideoRecorder />}
			{uploadStep === 'submit' && <VideoUpload />}
			{uploadStep === 'success' && (
				<Redirect to={buildVideoConfirmationUrl({ slug, videoId: uploadedVideoId, nextStep })} />
			)}
		</>
	);
};
